import React, { useState, useEffect } from 'react';
import { 
  XMarkIcon,
  CheckIcon,
  QrCodeIcon,
  MapPinIcon,
  WrenchScrewdriverIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import { CheckCircleIcon } from '@heroicons/react/24/solid';
import type { ServiceItem, ProposalItem, ItemSelectionAction, ItemSelectionStatus } from '../types';
import { proposalsApi } from '../services/api';

interface ItemSelectionModalProps {
  proposalItem: ProposalItem;
  isOpen: boolean;
  onClose: () => void;
  onSelectionComplete: () => void;
}

const ItemSelectionModal: React.FC<ItemSelectionModalProps> = ({
  proposalItem,
  isOpen,
  onClose,
  onSelectionComplete
}) => {
  const [availableItems, setAvailableItems] = useState<ServiceItem[]>([]);
  const [selectionStatus, setSelectionStatus] = useState<ItemSelectionStatus | null>(null);
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');

  useEffect(() => {
    if (isOpen && proposalItem) {
      loadData();
    }
  }, [isOpen, proposalItem]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Carregar itens disponíveis e status atual
      const [itemsResponse, statusResponse] = await Promise.all([
        proposalsApi.getAvailableItems(proposalItem.id),
        proposalsApi.getSelectionStatus(proposalItem.id)
      ]);
      
      setAvailableItems(itemsResponse);
      setSelectionStatus(statusResponse);
      
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAutoSelect = async () => {
    try {
      setLoading(true);
      
      const action: ItemSelectionAction = { action: 'auto_select' };
      const response = await proposalsApi.selectItems(proposalItem.id, action);
      
      alert(`✅ ${response.message}`);
      await loadData();
      onSelectionComplete();
      
    } catch (error: any) {
      console.error('Erro na seleção automática:', error);
      alert(`❌ ${error.response?.data?.error || 'Erro na seleção automática'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleManualSelect = async () => {
    if (selectedItems.size === 0) {
      alert('Selecione pelo menos um item');
      return;
    }

    try {
      setLoading(true);
      
      const action: ItemSelectionAction = {
        action: 'manual_select',
        service_items: Array.from(selectedItems)
      };
      
      const response = await proposalsApi.selectItems(proposalItem.id, action);
      
      alert(`✅ ${response.message}`);
      setSelectedItems(new Set());
      await loadData();
      onSelectionComplete();
      
    } catch (error: any) {
      console.error('Erro na seleção manual:', error);
      alert(`❌ ${error.response?.data?.error || 'Erro na seleção manual'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleClearSelection = async () => {
    if (!window.confirm('Deseja limpar toda a seleção atual?')) return;

    try {
      setLoading(true);
      
      const action: ItemSelectionAction = { action: 'clear_selection' };
      const response = await proposalsApi.selectItems(proposalItem.id, action);
      
      alert(`✅ ${response.message}`);
      await loadData();
      onSelectionComplete();
      
    } catch (error: any) {
      console.error('Erro ao limpar seleção:', error);
      alert(`❌ ${error.response?.data?.error || 'Erro ao limpar seleção'}`);
    } finally {
      setLoading(false);
    }
  };

  const toggleItemSelection = (itemId: string) => {
    const newSelected = new Set(selectedItems);
    
    if (newSelected.has(itemId)) {
      newSelected.delete(itemId);
    } else {
      // Verificar limitador
      const remainingNeeded = (selectionStatus?.total_quantity || 0) - (selectionStatus?.selected_count || 0);
      if (newSelected.size >= remainingNeeded) {
        alert(`Máximo permitido: ${remainingNeeded} itens`);
        return;
      }
      newSelected.add(itemId);
    }
    
    setSelectedItems(newSelected);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'available': return 'bg-green-100 text-green-800 border-green-200';
      case 'maintenance': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'rented': return 'bg-red-100 text-red-800 border-red-200';
      case 'damaged': return 'bg-red-100 text-red-800 border-red-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'available': return <CheckCircleIcon className="h-3 w-3" />;
      case 'maintenance': return <WrenchScrewdriverIcon className="h-3 w-3" />;
      case 'damaged': return <ExclamationTriangleIcon className="h-3 w-3" />;
      default: return null;
    }
  };

  if (!isOpen || !proposalItem) return null;

  const remainingNeeded = (selectionStatus?.total_quantity || 0) - (selectionStatus?.selected_count || 0);

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 z-40"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="fixed inset-0 flex items-center justify-center p-2 z-50">
        <div className="bg-white rounded-lg shadow-xl w-[95%] max-w-[1600px] max-h-[92vh] overflow-hidden">

          {/* Header */}
          <div className="flex items-center justify-between px-3 py-2 border-b border-gray-200 bg-gray-50">
            <div>
              <h2 className="text-sm font-semibold text-gray-900">
                Seleção de Itens - {proposalItem.service_name}
              </h2>
              <p className="text-xs text-gray-600 mt-0.5">
                Total: {selectionStatus?.total_quantity} |
                Selecionados: {selectionStatus?.selected_count} |
                Restante: {remainingNeeded}
              </p>
            </div>
            
            <div className="flex items-center gap-1">
              {/* Toggle View Mode */}
              <div className="flex border border-gray-200 rounded text-xs">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`px-2 py-1 ${viewMode === 'grid' ? 'bg-blue-100 text-blue-800' : 'text-gray-600'}`}
                >
                  Grade
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`px-2 py-1 ${viewMode === 'list' ? 'bg-blue-100 text-blue-800' : 'text-gray-600'}`}
                >
                  Lista
                </button>
              </div>
              
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 p-1"
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-2 px-3 py-1.5 bg-gray-50 border-b">
            <button
              onClick={handleAutoSelect}
              disabled={loading || remainingNeeded <= 0}
              className="bg-blue-600 text-white px-3 py-1 rounded text-xs font-medium hover:bg-blue-700 disabled:opacity-50"
            >
              🤖 Auto-Seleção ({remainingNeeded})
            </button>

            <button
              onClick={handleManualSelect}
              disabled={loading || selectedItems.size === 0}
              className="bg-green-600 text-white px-3 py-1 rounded text-xs font-medium hover:bg-green-700 disabled:opacity-50"
            >
              ✋ Selecionar ({selectedItems.size})
            </button>

            <button
              onClick={handleClearSelection}
              disabled={loading || (selectionStatus?.selected_count || 0) === 0}
              className="bg-red-600 text-white px-3 py-1 rounded text-xs font-medium hover:bg-red-700 disabled:opacity-50"
            >
              🗑️ Limpar Tudo
            </button>

            <div className="text-xs text-gray-600 ml-auto font-medium">
              {availableItems.length} itens disponíveis
            </div>
          </div>

          {/* Content */}
          <div className="max-h-[calc(92vh-160px)] overflow-y-auto px-3 py-2">
            {loading ? (
              <div className="flex items-center justify-center p-4">
                <div className="text-sm text-gray-600">Carregando...</div>
              </div>
            ) : viewMode === 'grid' ? (
              // Grid View (Tipo Cinema)
              <div className="grid grid-cols-8 gap-1">
                {availableItems.map((item) => {
                  const isSelected = selectedItems.has(item.id);
                  const isDisabled = remainingNeeded <= 0 && !isSelected;
                  
                  return (
                    <button
                      key={item.id}
                      onClick={() => !isDisabled && toggleItemSelection(item.id)}
                      disabled={isDisabled}
                      className={`
                        aspect-square p-1 border-2 rounded text-xs font-medium transition-all
                        ${isSelected 
                          ? 'border-blue-500 bg-blue-100 text-blue-800' 
                          : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'
                        }
                        ${isDisabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                      `}
                      title={`${item.internal_code}\nStatus: ${item.status_display}\nLocal: ${item.current_location}`}
                    >
                      <div className="flex flex-col h-full">
                        {isSelected && (
                          <CheckIcon className="h-3 w-3 text-blue-600 mb-1" />
                        )}
                        <div className="text-xs font-bold">{item.internal_code}</div>
                        <div className="text-xs opacity-75">{getStatusIcon(item.status)}</div>
                      </div>
                    </button>
                  );
                })}
              </div>
            ) : (
              // List View - Compacta
              <div className="space-y-0.5">
                {availableItems.map((item) => {
                  const isSelected = selectedItems.has(item.id);
                  const isDisabled = remainingNeeded <= 0 && !isSelected;

                  return (
                    <div
                      key={item.id}
                      className={`
                        flex items-center gap-2 px-2 py-1.5 border rounded cursor-pointer transition-all
                        ${isSelected
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50'
                        }
                        ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''}
                      `}
                      onClick={() => !isDisabled && toggleItemSelection(item.id)}
                    >
                      <div className="flex-shrink-0">
                        <div className={`w-3.5 h-3.5 border-2 rounded flex items-center justify-center ${
                          isSelected ? 'border-blue-500 bg-blue-500' : 'border-gray-300'
                        }`}>
                          {isSelected && <CheckIcon className="h-2.5 w-2.5 text-white" />}
                        </div>
                      </div>

                      <div className="flex-1 flex items-center gap-3 text-xs">
                        <div className="font-semibold text-gray-900 w-24">{item.internal_code}</div>
                        <div className="flex items-center gap-1 text-gray-600 w-32">
                          <QrCodeIcon className="h-3 w-3 text-gray-400 flex-shrink-0" />
                          <span>{item.qr_code.slice(-8)}</span>
                        </div>
                        <div className="flex items-center gap-1 text-gray-600 flex-1 min-w-0">
                          <MapPinIcon className="h-3 w-3 text-gray-400 flex-shrink-0" />
                          <span className="truncate">{item.current_location || 'Não informado'}</span>
                        </div>
                        <div className="flex items-center flex-shrink-0">
                          <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium border ${getStatusColor(item.status)}`}>
                            {getStatusIcon(item.status)}
                            <span className="ml-1">{item.status_display}</span>
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            
            {availableItems.length === 0 && !loading && (
              <div className="text-center py-4">
                <div className="text-sm text-gray-500">Nenhum item disponível</div>
              </div>
            )}
          </div>
          
          {/* Selected Items Summary */}
          {selectionStatus && selectionStatus.selected_items.length > 0 && (
            <div className="border-t px-3 py-2 bg-gray-50">
              <div className="text-xs font-semibold text-gray-900 mb-1.5">
                Itens Já Selecionados ({selectionStatus.selected_items.length}):
              </div>
              <div className="flex flex-wrap gap-1.5">
                {selectionStatus.selected_items.map((selection) => (
                  <span
                    key={selection.id}
                    className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800 border border-green-200"
                    title={`Selecionado ${selection.selected_automatically ? 'automaticamente' : 'manualmente'} por ${selection.selected_by_name || 'Sistema'}`}
                  >
                    {selection.service_item_code}
                    {selection.selected_automatically && ' 🤖'}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default ItemSelectionModal;